import { motion, AnimatePresence } from 'framer-motion';
import { X, List, LayoutGrid, SlidersHorizontal } from 'lucide-react';
import { HudIconButton } from '../ui/toolbar';
import { PageHeader } from '../ui/PageHeader';

/**
 * Directory page header: title and people count, with saved filter pills on
 * the line below. View controls (list/card, sort & filter, select-all) used
 * to live here too, but now sit in the row with the Directory/Anniversaries
 * tabs instead — see `ViewControls` below — so they read as one pill family
 * instead of a separate corner toolbar.
 *
 * The search input itself and the department quick-filter pills used to live
 * here; both moved out — search into the sidebar (see Layout.jsx's
 * SidebarSearch) for consistency across every view, and department pills were
 * dropped since the Filter drawer already offers the full department list.
 *
 * Fully token-driven — no useIsDark() branch. Both themes come from the token
 * block in src/index.css.
 */
export function SearchBar({
  totalCount = 0,
  filteredCount = 0,
  savedFilters = [],
  onDeleteFilter,
  onApplyFilter,
  title = 'Directory',
}) {
  return (
    <div className="w-full">

      {/* ── Page header ──────────────────────────────────────────────────────
          Was a 24px-radius glass panel with a 20px blur and a 52px shadow, which
          read as a consumer app and floated the whole toolbar off the page. Now a
          real page header: title and total on the left, controls on the right. */}
      <PageHeader title={title}>
        {filteredCount !== totalCount ? (
          <>
            <b className="font-mono font-semibold text-ink-2">{filteredCount.toLocaleString()}</b>
            {' of '}
            <b className="font-mono font-semibold text-ink-2">{totalCount.toLocaleString()}</b>
            {' people'}
          </>
        ) : (
          <>
            <b className="font-mono font-semibold text-ink-2">{totalCount.toLocaleString()}</b>
            {' people'}
          </>
        )}
      </PageHeader>

      {/* ── Sub-row: saved filters ─────────────────────────────────────────── */}
      {savedFilters.length > 0 && onApplyFilter ? (
        <div className="flex items-center gap-1.5 flex-wrap px-1 mt-2">
          <AnimatePresence>
            {savedFilters.map((filter) => (
              <motion.span
                key={filter.id}
                initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }}
                className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full text-[10px] font-medium
                           border border-accent text-accent"
              >
                <button onClick={() => onApplyFilter?.(filter)}>{filter.name}</button>
                <button
                  onClick={() => onDeleteFilter?.(filter.id)}
                  className="p-0.5 rounded-full transition-colors hover:bg-accent hover:text-[var(--on-accent)]"
                >
                  <X size={10} strokeWidth={2.5} />
                </button>
              </motion.span>
            ))}
          </AnimatePresence>
        </div>
      ) : null}
    </div>
  );
}

/**
 * List/Card toggle + Sort & Filter + Select-all, as one pill family sitting
 * in the same row as the Directory/Anniversaries tabs — moved out of the
 * SearchBar header's top-right corner so every control on that row reads as
 * the same rounded-pill language instead of two different toolbar styles.
 */
export function ViewControls({
  displayMode = 'card',
  onDisplayModeChange,
  selectedEmployees = [],
  onSelectAll,
  onDeselectAll,
  totalCount = 0,
  onOpenFilterDrawer,
  isFilterDrawerOpen = false,
  activeFilterCount = 0,
  onClearAll,
}) {
  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      {onSelectAll && onDeselectAll && totalCount > 0 && (
        <button
          onClick={selectedEmployees.length > 0 ? onDeselectAll : onSelectAll}
          className={`text-[10.5px] font-semibold whitespace-nowrap transition-colors px-2 py-1.5 rounded-ctl hover:bg-surface-2
            ${selectedEmployees.length > 0 ? 'text-ink-3' : 'text-accent'}`}
        >
          {selectedEmployees.length > 0 ? `Deselect ${selectedEmployees.length}` : 'Select all'}
        </button>
      )}

      {onDisplayModeChange ? (
        <div className="flex items-center gap-1.5">
          <HudIconButton
            pill
            title="List view"
            onClick={() => onDisplayModeChange('list')}
            active={displayMode === 'list'}
          >
            <List size={15} />
          </HudIconButton>
          <HudIconButton
            pill
            title="Card view"
            onClick={() => onDisplayModeChange('card')}
            active={displayMode === 'card'}
          >
            <LayoutGrid size={15} />
          </HudIconButton>
        </div>
      ) : null}

      {onOpenFilterDrawer ? (
        <div className="relative flex-none" data-filter-toggle>
          <HudIconButton
            pill
            title="Sort & Filter"
            onClick={onOpenFilterDrawer}
            active={isFilterDrawerOpen || activeFilterCount > 0}
          >
            <SlidersHorizontal size={15} />
          </HudIconButton>
          {activeFilterCount > 0 && onClearAll ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClearAll(); }}
              title="Clear all filters"
              aria-label="Clear all filters"
              className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center
                         bg-red-400 hover:bg-red-500 text-white transition-colors"
            >
              <X size={10} strokeWidth={3} />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
